// docs/189 EO2: how each thing stands on the board — its footprint (squares across and down from its own
// top-left square), whether it hangs on a wall, lies flat, hangs overhead or is a lamp. The sim's word
// (`thing_shapes` from wasm); a kind wasm has not told us of yet stands on one square. The footprint maths
// is pure so the renderer, the pointer and the tests share it.
import { wasmModule } from '../sim/wasm';
import type { ThingKind, ThingView, Tile } from '../sim/types';

export interface ThingShape {
  key: string;
  w: number;
  h: number;
  solid: boolean;
  /** hangs on a wall square, over the floor below it */
  wall: boolean;
  /** lies flat, walked over */
  flat: boolean;
  /** hangs overhead (a chandelier, icicles) */
  hangs: boolean;
  /** a light put out and lit again */
  lamp: boolean;
  flammable: boolean;
  rolls: boolean;
  material: string;
}

const ONE: Omit<ThingShape, 'key'> = { w: 1, h: 1, solid: true, wall: false, flat: false, hangs: false, lamp: false, flammable: false, rolls: false, material: 'Wood' };

let shapes: Map<string, ThingShape> | undefined;

/** For tests and harnesses without wasm: the shapes to use. */
export function setThingShapes(list: readonly ThingShape[] | undefined): void {
  shapes = list ? new Map(list.map((s) => [s.key, s])) : undefined;
}

function load(): void {
  if (shapes) return;
  try { setThingShapes(wasmModule().thing_shapes() as ThingShape[]); } catch { /* wasm not ready: one square for now */ }
}

export function thingShape(kind: ThingKind | string): ThingShape {
  load();
  return shapes?.get(kind) ?? { key: kind, ...ONE };
}

/** The squares a thing of `shape` set at `tile` stands on, row by row. */
export function footprint(tile: Tile, shape: Pick<ThingShape, 'w' | 'h'>): Tile[] {
  const out: Tile[] = [];
  for (let y = tile.y; y < tile.y + shape.h; y++) for (let x = tile.x; x < tile.x + shape.w; x++) out.push({ x, y });
  return out;
}

/** Whether a thing of `shape` at `tile` stands on `t`. */
export function covers(tile: Tile, shape: Pick<ThingShape, 'w' | 'h'>, t: Tile): boolean {
  return t.x >= tile.x && t.x < tile.x + shape.w && t.y >= tile.y && t.y < tile.y + shape.h;
}

/** Whether the thing `v` stands on `t` (every square of its footprint is it). */
export function thingCovers(v: Pick<ThingView, 'tile' | 'kind'>, t: Tile): boolean {
  return covers(v.tile, thingShape(v.kind), t);
}

/**
 * Where a thing's drawing is set, in board pixels: the bottom middle of its footprint, `lift` art pixels
 * above its bottom edge (its feet). A wall thing is set on the wall's face: the middle of its wall square,
 * its top `dressY` art pixels from the square's top (docs/164 `wall_dressing_y`).
 */
export function anchorPx(tile: Tile, shape: Pick<ThingShape, 'w' | 'h' | 'wall'>, cell: number, lift = 2, dressY = -4): { x: number; y: number; origin: [number, number] } {
  const k = cell / 24;
  const x = (tile.x + shape.w / 2) * cell;
  if (shape.wall) return { x, y: tile.y * cell + dressY * k, origin: [0.5, 0] };
  return { x, y: (tile.y + shape.h) * cell - lift * k, origin: [0.5, 1] };
}

/**
 * The sort depth of a standing thing, as the terrain's standing images are sorted (docs/186 EA3): the middle of
 * its footprint's bottom row, so a figure on the row below is drawn in front and one above it behind. A wall thing
 * sits just in front of its wall square's own image (the painter's `after(y)`).
 */
export function standDepth(tile: Tile, shape: Pick<ThingShape, 'h' | 'wall'>, cell: number): number {
  if (shape.wall) return (tile.y + 1) * cell + cell / 2 + 0.012;
  return (tile.y + shape.h - 1) * cell + cell / 2;
}

/** A stable variant for a thing (the same on every client): its id hashed, mod the variants it has. */
export function variantOf(id: number, count: number): number {
  if (count <= 1) return 0;
  let h = Math.imul(id | 0, 2654435761) >>> 0;
  h = (h ^ (h >>> 15)) >>> 0;
  h = Math.imul(h, 2246822519) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  return h % count;
}

/** The square of thing `v` nearest `t` (what a blow from `t` aims at). */
export function nearestSquare(v: Pick<ThingView, 'tile' | 'kind'>, t: Tile): Tile {
  const s = thingShape(v.kind);
  return { x: Math.min(Math.max(t.x, v.tile.x), v.tile.x + s.w - 1), y: Math.min(Math.max(t.y, v.tile.y), v.tile.y + s.h - 1) };
}
